import { PropAsset } from './PropAsset';

// Elevation / Platform - Yükselti / Platform
// Raised platform that player can walk on
export class Elevation extends PropAsset {
  readonly id = 'elevation';
  readonly width = 80;
  readonly height = 50;
  readonly walkable = false; // Blocks walking at base, but provides elevated walkable area
  readonly collisionRadius = 1.2;
  readonly occludesPlayer = true;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    // Colors
    const elevColors = [
      { top: '#7a8a7a', side: '#5a6a5a', dark: '#4a5a4a', grass: '#5a8a5a' }, // Grassy
      { top: '#9a9080', side: '#7a7060', dark: '#5a5040', grass: null },       // Stone
      { top: '#8a7a6a', side: '#6a5a4a', dark: '#4a3a2a', grass: null },       // Dirt
    ];
    const colors = elevColors[v % elevColors.length];

    const platformWidth = 70 * s;
    const platformDepth = 50 * s;
    const platformHeight = 30 * s;

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.ellipse(cx, cy + 5 * s, platformWidth / 2, platformDepth / 4, 0, 0, Math.PI * 2);
    ctx.fill();

    // Left face (darkest)
    ctx.fillStyle = colors.dark;
    ctx.beginPath();
    ctx.moveTo(cx - platformWidth / 2, cy);
    ctx.lineTo(cx - platformWidth / 2, cy - platformHeight);
    ctx.lineTo(cx, cy - platformHeight - platformDepth / 4);
    ctx.lineTo(cx, cy - platformDepth / 4);
    ctx.closePath();
    ctx.fill();

    // Right face
    ctx.fillStyle = colors.side;
    ctx.beginPath();
    ctx.moveTo(cx, cy - platformDepth / 4);
    ctx.lineTo(cx, cy - platformHeight - platformDepth / 4);
    ctx.lineTo(cx + platformWidth / 2, cy - platformHeight);
    ctx.lineTo(cx + platformWidth / 2, cy);
    ctx.closePath();
    ctx.fill();

    // Top surface
    ctx.fillStyle = colors.top;
    ctx.beginPath();
    ctx.moveTo(cx - platformWidth / 2, cy - platformHeight);
    ctx.lineTo(cx, cy - platformHeight - platformDepth / 2);
    ctx.lineTo(cx + platformWidth / 2, cy - platformHeight);
    ctx.lineTo(cx, cy - platformHeight + platformDepth / 4);
    ctx.closePath();
    ctx.fill();

    // Grass on top (for grassy variant)
    if (colors.grass) {
      ctx.fillStyle = colors.grass;
      // Small grass tufts
      for (let i = 0; i < 5; i++) {
        const gx = cx + (Math.random() - 0.5) * platformWidth * 0.6;
        const gy = cy - platformHeight - platformDepth / 4 + (Math.random() - 0.5) * platformDepth * 0.3;
        const gh = 4 * s + Math.random() * 3 * s;

        ctx.beginPath();
        ctx.moveTo(gx - 2 * s, gy);
        ctx.lineTo(gx, gy - gh);
        ctx.lineTo(gx + 2 * s, gy);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Edge details
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 1 * s;

    // Horizontal line on side
    ctx.beginPath();
    ctx.moveTo(cx, cy - platformHeight / 2 - platformDepth / 4);
    ctx.lineTo(cx + platformWidth / 2, cy - platformHeight / 2);
    ctx.stroke();

    // Top edge highlight
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.beginPath();
    ctx.moveTo(cx - platformWidth / 2, cy - platformHeight);
    ctx.lineTo(cx, cy - platformHeight - platformDepth / 2);
    ctx.lineTo(cx + platformWidth / 2, cy - platformHeight);
    ctx.stroke();
  }
}

// Cliff / Ledge - Uçurum / Kenar
export class Cliff extends PropAsset {
  readonly id = 'cliff';
  readonly width = 100;
  readonly height = 90;
  readonly walkable = false;
  readonly collisionRadius = 1.5;
  readonly occludesPlayer = true;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    const cliffColors = [
      { rock: '#7a7a7a', dark: '#5a5a5a', light: '#9a9a9a' },
      { rock: '#8a7a6a', dark: '#6a5a4a', light: '#aa9a8a' },
    ];
    const colors = cliffColors[v % cliffColors.length];

    const cliffWidth = 90 * s;
    const cliffHeight = 70 * s;

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.beginPath();
    ctx.ellipse(cx, cy + 5 * s, cliffWidth / 2, 20 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // Main cliff face (jagged)
    ctx.fillStyle = colors.rock;
    ctx.beginPath();
    ctx.moveTo(cx - cliffWidth / 2, cy);

    // Jagged left edge going up
    ctx.lineTo(cx - cliffWidth / 2 + 5 * s, cy - cliffHeight * 0.3);
    ctx.lineTo(cx - cliffWidth / 2 - 3 * s, cy - cliffHeight * 0.5);
    ctx.lineTo(cx - cliffWidth / 2 + 8 * s, cy - cliffHeight * 0.7);
    ctx.lineTo(cx - cliffWidth / 4, cy - cliffHeight);

    // Top edge (rough)
    ctx.lineTo(cx - 5 * s, cy - cliffHeight - 10 * s);
    ctx.lineTo(cx + 10 * s, cy - cliffHeight - 5 * s);
    ctx.lineTo(cx + cliffWidth / 4, cy - cliffHeight);

    // Jagged right edge going down
    ctx.lineTo(cx + cliffWidth / 2 - 5 * s, cy - cliffHeight * 0.6);
    ctx.lineTo(cx + cliffWidth / 2, cy - cliffHeight * 0.3);
    ctx.lineTo(cx + cliffWidth / 2 - 8 * s, cy);

    ctx.closePath();
    ctx.fill();

    // Dark crevices
    ctx.fillStyle = colors.dark;
    ctx.beginPath();
    ctx.moveTo(cx - 10 * s, cy - 10 * s);
    ctx.lineTo(cx - 15 * s, cy - cliffHeight * 0.5);
    ctx.lineTo(cx - 5 * s, cy - cliffHeight * 0.4);
    ctx.lineTo(cx, cy - 15 * s);
    ctx.closePath();
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(cx + 5 * s, cy - 20 * s);
    ctx.lineTo(cx + 10 * s, cy - cliffHeight * 0.6);
    ctx.lineTo(cx + 20 * s, cy - cliffHeight * 0.5);
    ctx.lineTo(cx + 15 * s, cy - 25 * s);
    ctx.closePath();
    ctx.fill();

    // Light highlights
    ctx.fillStyle = colors.light;
    ctx.beginPath();
    ctx.moveTo(cx - cliffWidth / 4 - 5 * s, cy - cliffHeight + 5 * s);
    ctx.lineTo(cx - cliffWidth / 4, cy - cliffHeight - 5 * s);
    ctx.lineTo(cx - cliffWidth / 4 + 10 * s, cy - cliffHeight);
    ctx.lineTo(cx - cliffWidth / 4 + 5 * s, cy - cliffHeight + 10 * s);
    ctx.closePath();
    ctx.fill();

    // Grass on top
    ctx.fillStyle = '#5a8a5a';
    for (let i = 0; i < 4; i++) {
      const gx = cx - cliffWidth / 4 + i * 15 * s;
      const gy = cy - cliffHeight - 5 * s + Math.sin(i) * 5 * s;

      ctx.beginPath();
      ctx.moveTo(gx - 3 * s, gy);
      ctx.lineTo(gx, gy - 8 * s);
      ctx.lineTo(gx + 3 * s, gy);
      ctx.closePath();
      ctx.fill();
    }
  }
}

// Pillar / Column - Sütun
export class Pillar extends PropAsset {
  readonly id = 'pillar';
  readonly width = 40;
  readonly height = 100;
  readonly walkable = false;
  readonly collisionRadius = 0.6;
  readonly occludesPlayer = true;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    const pillarColors = [
      { main: '#c0c0c0', dark: '#909090', light: '#e0e0e0' }, // Marble
      { main: '#8a8070', dark: '#5a5040', light: '#aaa090' }, // Sandstone
      { main: '#606060', dark: '#404040', light: '#808080' }, // Dark stone
    ];
    const colors = pillarColors[v % pillarColors.length];

    const pillarWidth = 24 * s;
    const pillarHeight = 80 * s;
    const baseWidth = 30 * s;
    const baseHeight = 8 * s;
    const capHeight = 10 * s;

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.ellipse(cx, cy - 2 * s, 18 * s, 8 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // Base
    ctx.fillStyle = colors.dark;
    ctx.fillRect(cx - baseWidth / 2, cy - baseHeight, baseWidth, baseHeight);
    ctx.fillStyle = colors.light;
    ctx.beginPath();
    ctx.moveTo(cx - baseWidth / 2, cy - baseHeight);
    ctx.lineTo(cx - baseWidth / 2 + 5 * s, cy - baseHeight - 3 * s);
    ctx.lineTo(cx + baseWidth / 2 - 5 * s, cy - baseHeight - 3 * s);
    ctx.lineTo(cx + baseWidth / 2, cy - baseHeight);
    ctx.closePath();
    ctx.fill();

    // Main shaft
    ctx.fillStyle = colors.main;
    ctx.fillRect(cx - pillarWidth / 2, cy - baseHeight - pillarHeight, pillarWidth, pillarHeight);

    // Shaft shading
    ctx.fillStyle = colors.dark;
    ctx.fillRect(cx - pillarWidth / 2, cy - baseHeight - pillarHeight, pillarWidth / 4, pillarHeight);
    ctx.fillStyle = colors.light;
    ctx.fillRect(cx + pillarWidth / 4, cy - baseHeight - pillarHeight, pillarWidth / 4, pillarHeight);

    // Fluting (vertical grooves)
    ctx.strokeStyle = 'rgba(0,0,0,0.1)';
    ctx.lineWidth = 1 * s;
    for (let i = 1; i < 4; i++) {
      const fx = cx - pillarWidth / 2 + (i / 4) * pillarWidth;
      ctx.beginPath();
      ctx.moveTo(fx, cy - baseHeight);
      ctx.lineTo(fx, cy - baseHeight - pillarHeight);
      ctx.stroke();
    }

    // Capital (top piece)
    const capY = cy - baseHeight - pillarHeight;
    ctx.fillStyle = colors.main;
    ctx.fillRect(cx - baseWidth / 2, capY - capHeight, baseWidth, capHeight);

    // Capital top
    ctx.fillStyle = colors.light;
    ctx.beginPath();
    ctx.moveTo(cx - baseWidth / 2, capY - capHeight);
    ctx.lineTo(cx - baseWidth / 2 + 5 * s, capY - capHeight - 4 * s);
    ctx.lineTo(cx + baseWidth / 2 - 5 * s, capY - capHeight - 4 * s);
    ctx.lineTo(cx + baseWidth / 2, capY - capHeight);
    ctx.closePath();
    ctx.fill();

    // Cracks for ruined variant
    if (v === 2) {
      ctx.strokeStyle = colors.dark;
      ctx.lineWidth = 1.5 * s;
      ctx.beginPath();
      ctx.moveTo(cx - 5 * s, cy - baseHeight - pillarHeight * 0.3);
      ctx.lineTo(cx + 2 * s, cy - baseHeight - pillarHeight * 0.5);
      ctx.lineTo(cx - 3 * s, cy - baseHeight - pillarHeight * 0.7);
      ctx.stroke();
    }
  }
}
